import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DeleteMode, GroupResponseV1, GroupType, GroupMemberResponseV1 } from '@access-management/access-management';
import { DeleteEntityDialog } from '@access-management/delete-entity-dialog/delete-entity-dialog';
import { InvitationResponseV1, InvitationStatus } from '@access-management/invitation';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { catchError, firstValueFrom, map, of, throwError } from 'rxjs';

import { AccessManagementService } from '@services/access-management';
import { InvitationService } from '@services/invitation';
import { MembershipService } from '@services/membership';

@Component({
  selector: 'app-household-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ButtonModule,
    CardModule,
    MessageModule,
    ProgressSpinnerModule,
    TagModule,
    DeleteEntityDialog
  ],
  templateUrl: './household-detail-page.html',
  styleUrl: './household-detail-page.scss'
})
export class HouseholdDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly accessManagementService = inject(AccessManagementService);
  private readonly invitationService = inject(InvitationService);
  private readonly membershipService = inject(MembershipService);
  private readonly messageService = inject(MessageService);

  readonly groupId = toSignal(this.route.paramMap.pipe(map((p) => p.get('groupId'))), { initialValue: null });

  readonly groupResource = rxResource<GroupResponseV1 | null, string | null>({
    params: () => this.groupId(),
    stream: ({ params }) => {
      if (!params) return of(null);
      return this.accessManagementService.getGroupById(params).pipe(
        map((g) => g ?? null),
        catchError((err) => throwError(() => (err instanceof Error ? err : new Error(String(err)))))
      );
    }
  });

  readonly membersResource = rxResource<GroupMemberResponseV1[], string | null>({
    params: () => this.groupId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.accessManagementService.getGroupMembers(params);
    }
  });

  readonly membershipsResource = this.membershipService.createGroupMembershipsResource(() => this.groupId());

  readonly invitationsResource = rxResource<InvitationResponseV1[], string | null>({
    params: () => this.groupId(),
    stream: ({ params }) => {
      if (!params) return of([]);
      return this.invitationService.getGroupInvitations(params);
    }
  });

  readonly hasMemberships = computed(() => (this.membershipsResource.value()?.memberships?.length ?? 0) > 0);

  readonly showDeleteDialog = signal(false);
  readonly deleting = signal(false);

  openDeleteDialog(): void {
    this.showDeleteDialog.set(true);
  }

  async onConfirmDelete(mode: DeleteMode): Promise<void> {
    const groupId = this.groupId();
    if (!groupId) return;

    this.deleting.set(true);
    try {
      await firstValueFrom(this.accessManagementService.deleteHousehold(groupId, mode));
      this.messageService.add({ severity: 'success', summary: 'Household deleted', detail: 'The household was deleted.' });
      this.showDeleteDialog.set(false);
      await this.router.navigate(['/access-management/groups']);
    } catch {
      this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: 'The household could not be deleted.' });
    } finally {
      this.deleting.set(false);
    }
  }

  // The wire value is the label. The reverse lookup this replaced only ever worked on a numeric enum,
  // and the engine names the type, so it indexed a string enum by its own value and answered Unknown.
  groupTypeLabel(type: GroupType | undefined | null): string {
    return type ?? 'Unknown';
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  }

  getInvitationStatusSeverity(status: InvitationStatus): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case InvitationStatus.Pending:
        return 'info';
      case InvitationStatus.Accepted:
        return 'success';
      case InvitationStatus.Declined:
      case InvitationStatus.Cancelled:
        return 'warn';
      case InvitationStatus.Expired:
        return 'danger';
      case InvitationStatus.PendingPurchase:
      default:
        return 'secondary';
    }
  }

  refreshAll(): void {
    this.groupResource.reload();
    this.membersResource.reload();
    this.membershipsResource.reload();
    this.invitationsResource.reload();
  }
}
